'use client';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ProductImages } from './product-images';
import { Breadcrumbs } from '../../homepage/breadcrumbs';
import { ProductVariants } from './variants/product-variants';
import { ProductDetailPrice } from './product-detail-price';
import { ProductDetailAvailability } from './product-detail-availability';
import { ProductQuantity } from './product-quantity';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { ProductPaymentMethods } from './product-payment-methods';
import { ProductAttributes } from '../product-attributes';
import { ProductBottomInfo } from './product-bottom-info';
import { ProductAccordionDescription } from './accordion/product-accordion-description';
import { ProductAccordionParameters } from './accordion/product-accordion-parameters';
import { ProductAccordionAttachments } from './accordion/product-accordion-attachments';
import { ProductAccordionReviews } from './accordion/product-accordion-reviews';
import { ProductHeader } from './product-header';
import { ProductShortDescription } from './product-short-description';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ProductAccordion } from './accordion/product-accordion';
import React from 'react';

interface ProductAccordionProps {
  title: string;
  key: string;
  content: React.ReactNode;
  show: boolean;
}

export function ProductInfo({ product }: { product: ProductDetailData }) {
  const { t } = useTranslations();
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const accordions = [
    {
      title: t('text231'),
      key: 'description',
      content: <ProductAccordionDescription />,
      show: !!selectedVariant?.description || !!product?.description,
    },
    {
      title: `${t('text9')} (cm)`,
      key: 'parameters',
      content: <ProductAccordionParameters />,
      show: true,
    },
    {
      title: t('text828'),
      key: 'attachments',
      content: <ProductAccordionAttachments />,
      show: product?.attachments && product?.attachments?.length,
    },
    // {
    //   title: t('text3399'),
    //   key: 'reviews',
    //   content: <ProductAccordionReviews />,
    //   show: true,
    // },
  ] as ProductAccordionProps[];

  return (
    <section
      className={cn('custom-section', 'gap-4 pb-16 pt-8 sm:pb-20 sm:pt-10 lg:pb-24 lg:pt-12')}
    >
      <div className={cn('custom-container')}>
        <Breadcrumbs breadcrumb={product.breadcrumb} product={product} />
      </div>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 md:flex-row md:justify-between lg:gap-12',
        )}
      >
        <CarouselProvider>
          <ProductImages />
        </CarouselProvider>
        <div className="flex w-full flex-col items-start justify-start gap-8 md:max-w-[545px]">
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            <ProductHeader />
            <ProductAttributes attributes={product.attributes} />
            <ProductShortDescription />
            <ProductVariants />
            <ProductDetailAvailability />
            <ProductDetailPrice />
            <ProductQuantity />
            <ProductPaymentMethods />
            <Separator />
            <ProductBottomInfo />
          </div>
          <Separator />
          <div className="flex w-full flex-col items-start justify-start gap-4">
            {accordions?.map((accordion, index) => {
              if (accordion?.show)
                return (
                  <React.Fragment key={accordion?.key}>
                    <ProductAccordion title={accordion.title} key={accordion?.key}>
                      {accordion?.content}
                    </ProductAccordion>
                    {accordions?.length !== index + 1 && <Separator />}
                  </React.Fragment>
                );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
